/* global BigInt */
// Shared by React and Next. Analytics observes confirmed outcomes; it never moves money.
export const GTM_ID = "GTM-N7QKB4TL";
export const MEASUREMENT_ID = "G-6WFSJVTJMV";
export const STAGING_MEASUREMENT_ID = "G-M65V2XK3DG";
const LIVE_HOST = "flytteogfragttilbud.dk";
const STAGING_HOST = "test.flytteogfragttilbud.dk";
const FIELDS = [
  "task_id",
  "bid_id",
  "booking_id",
  "transaction_id",
  "payout_id",
  "task_type",
  "booking_type",
  "value",
  "currency",
  "payment_status",
  "payout_status",
  "event_id",
  "booking_claimed",
];
const TYPES = ["moving", "courier", "package", "furniture", "fullload"];
const appTitles = {
  "/login": "Log ind",
  "/register": "Opret konto",
  "/profile": "Profil",
  "/dashboard": "Dashboard",
  "/BrowseTasks": "Find opgaver",
  "/tasks": "Find opgaver",
  "/create-task": "Book transport",
  "/payment-success": "Betalingsbekræftelse",
  "/support": "Support",
  "/app/support": "Support",
  "/legal/cmr": "CMR",
};
const publicPaths = [
  "/",
  "/about",
  "/faq",
  "/contact",
  "/how-it-works",
  "/safety",
  "/privacy-policy",
  "/terms-of-service",
  "/carrier-agreement",
  "/help",
  "/blog",
  "/services",
];
const state = () => window.__fofAnalytics || (window.__fofAnalytics = {});
const positive = (x) =>
  /^(?:[1-9][0-9]*)$/.test(String(x)) && Number.isSafeInteger(Number(x));
const entityId = (type, id) =>
  ["task", "instant_quote"].includes(type) && positive(id)
    ? `${type}:${id}`
    : null;

export function safePage(input, title, publicContent = false) {
  try {
    const url = new URL(input, "https://" + LIVE_HOST);
    if (
      /^\/(?:admin|admindashboard|studio|employer)(?:\/|$)/i.test(url.pathname)
    )
      return null;
    if (
      /^\/(?:admin|admindashboard|studio|employer)(?:\/|$)/i.test(
        url.searchParams.get("next") || "",
      )
    )
      return null;
    const pathname = url.pathname;
    const appPath = pathname.replace(/\/$/, "").toLowerCase();
    let label = Object.entries(appTitles).find(
      ([key]) => key.toLowerCase() === appPath,
    )?.[1];
    if (/^\/task\/(?:instant\/)?[1-9][0-9]*$/.test(appPath)) label = "Opgave";
    if (/^\/orders\/(?:task|instant_quote|instant)\/[1-9][0-9]*$/.test(appPath))
      label = "Booking";
    if (label)
      return {
        page_path: pathname,
        page_title: label + " | Flytte og Fragttilbud",
      };
    const isPublic =
      publicPaths.includes(pathname) ||
      /^\/(?:help|blog|services)\/[a-z0-9æøå-]+$/i.test(pathname) ||
      (publicContent && /^\/(?:seo\/)?[a-z0-9æøå-]+$/i.test(pathname));
    if (!isPublic || !title || /@|https?:|\b\d{8,}\b/.test(title)) return null;
    return { page_path: pathname, page_title: String(title).slice(0, 160) };
  } catch {
    return null;
  }
}
function internal() {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return (
      ["admin", "content_moderator"].includes(user?.role) ||
      sessionStorage.getItem("fof.analytics.internal") === "1"
    );
  } catch {
    return true;
  }
}
function allowed() {
  return (
    typeof window !== "undefined" &&
    !internal() &&
    !!safePage(window.location.href, document.title, state().publicContent) &&
    window.__fofGtmAnalyticsGranted === true &&
    window.Cookiebot?.consent?.statistics === true
  );
}

// Integer arithmetic only. Number conversion is confined to the GA wire boundary.
export function minorAnalyticsValue(input) {
  if (!/^(0|[1-9][0-9]*)$/.test(String(input))) return undefined;
  const amount = BigInt(input);
  if (amount > 1000000000n) return undefined;
  return Number(`${amount / 100n}.${String(amount % 100n).padStart(2, "0")}`);
}
export function decimalAnalyticsValue(input) {
  const match = /^(0|[1-9][0-9]*)(?:\.([0-9]{1,2}))?$/.exec(String(input));
  return match
    ? minorAnalyticsValue(
        BigInt(match[1]) * 100n + BigInt((match[2] || "").padEnd(2, "0")),
      )
    : undefined;
}
function sanitized(event, input) {
  const result = Object.fromEntries(FIELDS.map((key) => [key, null]));
  result.booking_claimed = input.booking_claimed === true;
  for (const key of ["task_id", "booking_id"])
    if (/^(task|instant_quote):[1-9][0-9]*$/.test(input[key]))
      result[key] = input[key];
  for (const [key, prefix] of [
    ["bid_id", "bid"],
    ["transaction_id", "transaction"],
    ["payout_id", "payout"],
  ]) {
    if (new RegExp(`^${prefix}:[1-9][0-9]*$`).test(input[key]))
      result[key] = input[key];
  }
  if (TYPES.includes(input.task_type)) result.task_type = input.task_type;
  if (["regular", "instant"].includes(input.booking_type))
    result.booking_type = input.booking_type;
  if (
    ["authorized", "captured", "credit_settled", "failed"].includes(
      input.payment_status,
    )
  )
    result.payment_status = input.payment_status;
  if (input.payout_status === "stripe_transfer_completed")
    result.payout_status = input.payout_status;
  if (
    input.currency === "DKK" &&
    typeof input.value === "number" &&
    Number.isFinite(input.value) &&
    input.value >= 0 &&
    decimalAnalyticsValue(input.value) === input.value
  ) {
    result.value = input.value;
    result.currency = "DKK";
  }
  let id;
  if (event === "task_created") id = result.task_id;
  if (event === "bid_sent")
    id = result.bid_id && result.task_id && result.bid_id;
  if (["booking_completed", "instant_booking_completed"].includes(event))
    id =
      result.booking_claimed &&
      result.booking_id &&
      result.transaction_id &&
      result.payment_status &&
      result.booking_id;
  if (event === "payment_failed")
    id =
      result.transaction_id &&
      result.payment_status === "failed" &&
      result.transaction_id;
  if (event === "payout")
    id = result.payout_id && result.payout_status && result.payout_id;
  if (!id) return null;
  result.event_id = `${event}:${id}`;
  return { event, ...result };
}
export function pushTrackingEvent(event, parameters = {}) {
  if (!allowed()) return;
  const payload = sanitized(event, parameters);
  if (!payload) return;
  const emit = () => {
    if (!allowed()) return;
    const key = "fof.analytics.v1." + payload.event_id;
    // Fail closed if persistent deduplication is unavailable; never block the action.
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, "1");
    } catch {
      return;
    }
    try {
      const page = safePage(
        window.location.href,
        document.title,
        state().publicContent,
      );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        ...payload,
        ...page,
        page_location: window.location.origin + page.page_path,
        page_referrer: state().referrer || "",
        environment:
          window.location.hostname === LIVE_HOST ? "production" : "staging",
        traffic_type: "customer",
      });
    } catch {
      /* Analytics failure must never turn a committed action into an API failure. */
    }
  };
  if (window.navigator.locks)
    return window.navigator.locks
      .request(
        "fof-analytics-" + payload.event_id,
        { ifAvailable: true },
        (lock) => {
          if (lock) emit();
        },
      )
      .catch(() => {});
  return emit();
}
// Compatibility callers cannot override the entity-based deduplication identity.
export function trackPaymentEvent(event, _key, parameters = {}) {
  return pushTrackingEvent(event, parameters);
}
export async function trackBookingResult(result, claimBooking) {
  const id = entityId(result?.entityType, result?.entityId);
  if (
    !allowed() || typeof claimBooking !== "function" || !id ||
    result.ok !== true ||
    !positive(result.transactionId) ||
    !["task_payment", "instant_quote"].includes(result.paymentKind) ||
    !["authorized", "paid", "credit_settled"].includes(result.status)
  )
    return;
  // Browser storage cannot deduplicate another browser or cleared storage.
  // Claim only after applied statistics consent. A lost claim response may
  // undercount; never replay a globally consumed claim or fail the booking.
  try {
    const claim = await claimBooking(result.transactionId);
    if (claim?.granted !== true || claim.bookingId !== id ||
        claim.transactionId !== result.transactionId) return;
  } catch {
    return;
  }
  const instant = result.entityType === "instant_quote";
  return pushTrackingEvent(
    instant ? "instant_booking_completed" : "booking_completed",
    {
      task_id: id,
      booking_id: id,
      booking_claimed: true,
      transaction_id: `transaction:${result.transactionId}`,
      bid_id: positive(result.bidId) ? `bid:${result.bidId}` : undefined,
      task_type: result.taskType,
      booking_type: instant ? "instant" : "regular",
      value: minorAnalyticsValue(result.chargedMinor),
      currency: "DKK",
      payment_status: result.status === "paid" ? "captured" : result.status,
    },
  );
}
export function trackPayoutResult(result) {
  const row = result?.payout || result;
  if (row?.status !== "paid" || !positive(row.id) || !row.financial_command_id)
    return;
  // This event is an observed Stripe Connect transfer, never a bank payout.
  if (!/^tr_[a-zA-Z0-9]+$/.test(row.stripe_transfer_id)) return;
  const id = row.instant_quote_id
    ? entityId("instant_quote", row.instant_quote_id)
    : entityId("task", row.task_id);
  if (!id) return;
  return pushTrackingEvent("payout", {
    payout_id: `payout:${row.id}`,
    task_id: id,
    booking_id: id,
    booking_type: row.instant_quote_id ? "instant" : "regular",
    value: decimalAnalyticsValue(row.amount),
    currency: "DKK",
    payout_status: "stripe_transfer_completed",
  });
}
export function trackPaymentFailure(error, attempt) {
  // A decline of an actual Stripe attempt, not validation/network/credit-grant errors.
  if (
    error?.type !== "card_error" ||
    !positive(attempt?.transactionId) ||
    !attempt.intentId ||
    (error.payment_intent?.id && error.payment_intent.id !== attempt.intentId)
  )
    return;
  return pushTrackingEvent("payment_failed", {
    transaction_id: `transaction:${attempt.transactionId}`,
    value: minorAnalyticsValue(attempt.chargedMinor),
    currency: "DKK",
    payment_status: "failed",
  });
}
export function trackPageView(path, title, publicContent = false) {
  if (typeof window === "undefined") return;
  const page = safePage(path, title, publicContent),
    current = state();
  current.pendingPage = page;
  current.publicContent = publicContent;
  if (!page || !allowed() || current.lastPage === page.page_path) return;
  const referrer = current.lastPage
    ? window.location.origin + current.lastPage
    : "";
  current.referrer = referrer;
  current.lastPage = page.page_path;
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "fof_page_view",
      ...page,
      page_location: window.location.origin + page.page_path,
      page_referrer: referrer,
      environment:
        window.location.hostname === LIVE_HOST ? "production" : "staging",
      traffic_type: "customer",
    });
  } catch {
    /* A blocked analytics script must not break navigation. */
  }
}
export function initializeAnalytics(role, publicContent = false) {
  if (typeof window === "undefined") return;
  const current = state();
  current.publicContent = publicContent;
  // The GTM bridge supplies the applied-consent flag. Cookiebot's DOM callback
  // can precede that update, so calling this alone cannot grant permission.
  window.__fofApplyAnalyticsConsent = () => {
    initializeAnalytics(undefined, state().publicContent);
    const pending = state().pendingPage;
    // A consent callback can arrive after history changed but before the router
    // committed its observer. Wait for that observer instead of replaying A on B.
    if (pending?.page_path === window.location.pathname)
      trackPageView(
        pending.page_path,
        pending.page_title,
        state().publicContent,
      );
  };
  if (["admin", "content_moderator"].includes(role)) {
    try {
      sessionStorage.setItem("fof.analytics.internal", "1");
    } catch {
      /* fail closed below */
    }
    window["ga-disable-" + MEASUREMENT_ID] = true;
    window["ga-disable-" + STAGING_MEASUREMENT_ID] = true;
    current.excluded = true;
    return;
  }
  const excluded =
    internal() ||
    !safePage(window.location.href, document.title, publicContent);
  current.excluded = excluded;
  const consentGranted =
    window.__fofGtmAnalyticsGranted === true &&
    window.Cookiebot?.consent?.statistics === true;
  window["ga-disable-" + MEASUREMENT_ID] =
    excluded || !consentGranted || window.location.hostname !== LIVE_HOST;
  window["ga-disable-" + STAGING_MEASUREMENT_ID] =
    excluded || !consentGranted || window.location.hostname !== STAGING_HOST;
  if (excluded) return;
  if (!current.listening) {
    current.listening = true;
    const consentChanged = () => window.__fofApplyAnalyticsConsent();
    window.addEventListener("CookiebotOnAccept", consentChanged);
    window.addEventListener("CookiebotOnConsentReady", consentChanged);
    window.addEventListener("CookiebotOnDecline", consentChanged);
    window.addEventListener("pageshow", (event) => {
      if (event.persisted) {
        current.lastPage = null;
        consentChanged();
      }
    });
  }
  if (current.loaded || current.loading) return;
  if (window.location.hostname === LIVE_HOST) {
    loadContainer("https://www.googletagmanager.com/gtm.js?id=" + GTM_ID);
  } else if (window.location.hostname === STAGING_HOST) {
    // Deployment supplies the supported public environment snippet separately.
    // Never fall back to the published live container on missing configuration.
    current.loading = true;
    window
      .fetch("/analytics-environment.json", {
        cache: "no-store",
        credentials: "omit",
        redirect: "error",
      })
      .then((response) => (response.ok ? response.json() : null))
      .then((config) => {
        if (
          config?.containerId !== GTM_ID ||
          config.measurementId !== STAGING_MEASUREMENT_ID ||
          config.propertyId !== "555139183" ||
          config.environmentId !== "env-72" ||
          !/^[A-Za-z0-9_-]{16,128}$/.test(config.authorization || "")
        )
          return;
        const url = new URL("https://www.googletagmanager.com/gtm.js");
        url.search = new URLSearchParams({
          id: GTM_ID,
          gtm_auth: config.authorization,
          gtm_preview: config.environmentId,
          gtm_cookies_win: "x",
        }).toString();
        loadContainer(url.href);
      })
      .catch(() => {
        // Analytics unavailability must never interrupt the application.
      })
      .finally(() => {
        current.loading = false;
      });
  }
}
function loadContainer(src) {
  const current = state();
  const page = safePage(
    window.location.href,
    document.title,
    current.publicContent,
  );
  // Configuration may finish after navigation or authentication has changed.
  if (current.loaded || current.excluded || internal() || !page) return;
  current.loaded = true;
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  gtag("set", {
    page_location: window.location.origin + page.page_path,
    page_referrer: "",
    page_title: page.page_title,
    send_page_view: false,
  });
  window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
  const script = document.createElement("script");
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}
